import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { NavigationEnd, Router }             from '@angular/router';
import { Subscription }                      from 'rxjs';
import { filter, startWith }                 from 'rxjs/operators';
import { CsChatMessage }                     from '@cs/components/chat';
import { AgentContextService }               from '../services/agent-context.service';
import { PmChatService, PmContextUsage }     from '../services/pm-chat.service';
import { PmChatConversation }                from '../services/pm-chat-conversation';
import { AI_ANALYST_ENABLED }                from '../ai-analyst.tokens';

/**
 * Floating "AI Data Analyst" chat bubble, global across the authenticated PM shell (mounted next to
 * <pmc-pm-deploy-info>). A launcher button toggles a compact panel embedding the shared <cs-chat>;
 * every question carries the live page context from {@link AgentContextService}, so quick "about
 * this page" questions need no set-up. "Expand" routes to the full AI Data Analyst page for a deep
 * dive. Modelled on the PmDeployInfoComponent widget pattern (fixed position, low chrome).
 *
 * Hidden on routes in {@link hideOnRoutes} (default: the full `/ai-analyst` page, where it would be
 * redundant). Pass a longer list from the host to suppress it elsewhere (login, error pages, …).
 */
@Component({
	standalone:      false,
	selector:        'pmc-ai-analyst-bubble',
	templateUrl:     './ai-analyst-bubble.component.html',
	styleUrls:       ['./ai-analyst-bubble.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiAnalystBubbleComponent implements OnInit, OnDestroy {

	/** URL prefixes on which the bubble is hidden. Matched against the path (query string ignored). */
	@Input() hideOnRoutes: string[] = ['/ai-analyst'];

	open = false;

	/** True when the current route matches {@link hideOnRoutes}, or the feature is off for this customer. */
	hidden = false;

	/**
	 * Whether to offer the advanced debug view (full system prompt + tool schemas + model config).
	 * Gated on the CF `Admin.Advanced` resource carried by the AI JWT; starts false and only flips
	 * once that claim is confirmed. UI gating only — MCP enforces the same resource server-side.
	 */
	canDebug = false;

	/** False when a customer has disabled the assistant via AI_ANALYST_ENABLED; the bubble never shows. */
	private readonly enabled: boolean;

	private readonly convo: PmChatConversation;
	private routerSub?: Subscription;

	constructor(
		private readonly chat: PmChatService,
		private readonly cdr: ChangeDetectorRef,
		private readonly agentContext: AgentContextService,
		private readonly router: Router,
		// OFF unless the customer opts in with { provide: AI_ANALYST_ENABLED, useValue: true }.
		// The token defaults to false, so absent/unprovided also means disabled.
		@Optional() @Inject(AI_ANALYST_ENABLED) enabled: boolean | null
	) {
		this.enabled = enabled === true;
		this.convo = new PmChatConversation(chat, cdr, {
			context: () => this.agentContext.snapshot()
		});
	}

	ngOnInit(): void {
		// Disabled for this customer: stay hidden and never subscribe to route changes.
		if (!this.enabled) { this.hidden = true; return; }

		// Resolve the admin entitlement once; stays false (control hidden) if the claim is absent.
		this.chat.canUseAdvancedDebug().then(allowed => {
			this.canDebug = allowed;
			this.cdr.markForCheck();
		});

		this.routerSub = this.router.events
			.pipe(filter(e => e instanceof NavigationEnd), startWith(null))
			.subscribe(() => {
				const path = this.router.url.split('?')[0].split('#')[0];
				const next = this.hideOnRoutes.some(r => path === r || path.startsWith(r + '/'));
				if (next !== this.hidden) {
					this.hidden = next;
					if (this.hidden) { this.open = false; }
					this.cdr.markForCheck();
				}
			});
	}

	get messages(): CsChatMessage[]        { return this.convo.messages; }
	get busy(): boolean                    { return this.convo.busy; }
	get contextUsage(): PmContextUsage | undefined { return this.convo.contextUsage; }
	get debugContext() { return this.convo.debugContext; }
	onDebugRequested(): void { this.convo.loadDebugContext(); }

	toggle(): void { this.open = !this.open; }
	close():  void { this.open = false; }

	onSend(text: string): void { this.convo.send(text); }
	onStop(): void             { this.convo.stop(); }

	/** Start a fresh conversation in the bubble. */
	newChat(): void { this.convo.reset(); }

	/** Open the full AI Data Analyst page for a deep dive. */
	expand(): void {
		this.close();
		void this.router.navigate(['/ai-analyst']);
	}

	ngOnDestroy(): void {
		this.routerSub?.unsubscribe();
		this.convo.destroy();
	}
}
